/**
 * Auth Plugin for Nuxt 3
 *
 * Initializes Firebase auth state listener using the useAuth composable
 * Following best practices from: https://dev.to/this-is-learning/firebase-authentication-from-zero-to-hero-4mj8
 */

import { useAuth } from '@/composables/auth/useAuth'
import { logger } from '@/utils/logger'

export default defineNuxtPlugin({
    name: 'auth',
    dependsOn: ['firebase'],
    async setup(nuxtApp) {
        // Only run on client
        if (!import.meta.client) {
            return
        }

        const { $auth, $firebase } = nuxtApp

        // Check if Firebase auth is initialized
        if (!$auth) {
            logger.error('[Auth Plugin] Firebase auth not initialized', nuxtApp)
            return
        }

        // Firestore is loaded on demand, and initAuth waits for it before loading a signed-in
        // user's profile. Start the download now if useAuth's hint cookie says this visitor was
        // signed in, so it runs while Firebase Auth restores the session instead of after it.
        if (useCookie('authHint').value) {
            $firebase.ensureFirestore().catch((error) => logger.warn('[Auth Plugin] Firestore preload failed', error))
        }

        // Initialize auth state listener through composable
        // Wait for the first auth state to resolve before continuing
        const { initAuth } = useAuth()
        const { useApplicationStore } = await import('@/stores/application')
        const store = useApplicationStore()

        // Create a promise that resolves after first auth state
        const authReady = new Promise<void>((resolve) => {
            // Watch for authInitialized to become true
            const unwatch = watch(
                () => store.authInitialized,
                (initialized) => {
                    if (initialized) {
                        unwatch()
                        resolve()
                    }
                },
                { immediate: true }
            )
        })

        const unsubscribe = await initAuth()

        await authReady

        // Clean up listener on app unmount
        if (unsubscribe) {
            nuxtApp.vueApp.onUnmount(() => {
                unsubscribe()
            })
        }
    }
})
